import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiMail, FiLock, FiUser, FiAlertCircle, FiEye, FiEyeOff } from 'react-icons/fi';
import { FcGoogle } from 'react-icons/fc';
import { useAuth } from '../context/AuthContext';

const Input = ({ icon: Icon, type = 'text', placeholder, value, onChange, right }) => (
  <div className="relative">
    <Icon size={16} className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted)' }} />
    <input
      type={type}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      required
      className="w-full pl-11 pr-11 py-3.5 rounded-xl text-sm font-body outline-none transition-all"
      style={{
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.08)',
        color: 'var(--text)',
      }}
      onFocus={e => e.target.style.borderColor = 'rgba(229,9,20,0.6)'}
      onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.08)'}
    />
    {right && <div className="absolute right-4 top-1/2 -translate-y-1/2">{right}</div>}
  </div>
);

const AuthPage = () => {
  const [mode, setMode]           = useState('login'); // 'login' | 'signup'
  const [name, setName]           = useState('');
  const [email, setEmail]         = useState('');
  const [password, setPassword]   = useState('');
  const [showPw, setShowPw]       = useState(false);
  const [error, setError]         = useState('');
  const [busy, setBusy]           = useState(false);
  const { signIn, signUp, signInGoogle } = useAuth();
  const navigate = useNavigate();

  const friendly = (code) => ({
    'auth/user-not-found':       'No account found with this email.',
    'auth/wrong-password':       'Incorrect password.',
    'auth/email-already-in-use': 'An account with this email already exists.',
    'auth/weak-password':        'Password must be at least 6 characters.',
    'auth/invalid-email':        'Please enter a valid email address.',
    'auth/popup-closed-by-user': 'Google sign-in was cancelled.',
  })[code] || 'Something went wrong. Please try again.';

  const handle = async (fn) => {
    setError(''); setBusy(true);
    try { await fn(); navigate('/'); }
    catch (e) { setError(friendly(e.code)); }
    finally { setBusy(false); }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (mode === 'login') handle(() => signIn(email, password));
    else handle(() => signUp(email, password, name));
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 relative overflow-hidden"
      style={{ background: 'var(--dark)' }}>

      {/* Background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(229,9,20,0.08) 0%, transparent 70%)' }} />

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md relative z-10"
      >
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'var(--red)' }}>
            <span className="font-display text-white text-base">C</span>
          </div>
          <span className="font-display text-3xl tracking-widest" style={{ color: 'var(--text)' }}>CINESPUR</span>
        </div>

        {/* Card */}
        <div className="rounded-2xl p-8" style={{
          background: 'rgba(17,17,24,0.9)',
          border: '1px solid rgba(255,255,255,0.07)',
          backdropFilter: 'blur(24px)',
          boxShadow: '0 40px 80px rgba(0,0,0,0.6)',
        }}>
          {/* Tab switcher */}
          <div className="flex rounded-xl p-1 mb-7" style={{ background: 'rgba(255,255,255,0.04)' }}>
            {['login', 'signup'].map(m => (
              <button key={m} onClick={() => { setMode(m); setError(''); }}
                className="flex-1 py-2.5 rounded-lg text-sm font-body font-medium transition-all duration-200 capitalize"
                style={{
                  background: mode === m ? 'var(--red)' : 'transparent',
                  color: mode === m ? '#fff' : 'var(--muted)',
                  boxShadow: mode === m ? '0 0 20px rgba(229,9,20,0.3)' : 'none',
                }}>
                {m === 'login' ? 'Sign In' : 'Sign Up'}
              </button>
            ))}
          </div>

          <form onSubmit={onSubmit} className="flex flex-col gap-4">
            <AnimatePresence>
              {mode === 'signup' && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                >
                  <Input icon={FiUser} placeholder="Full name" value={name} onChange={e => setName(e.target.value)} />
                </motion.div>
              )}
            </AnimatePresence>

            <Input icon={FiMail} type="email" placeholder="Email address"
              value={email} onChange={e => setEmail(e.target.value)} />

            <Input icon={FiLock} type={showPw ? 'text' : 'password'} placeholder="Password"
              value={password} onChange={e => setPassword(e.target.value)}
              right={
                <button type="button" onClick={() => setShowPw(s => !s)} style={{ color: 'var(--muted)' }}>
                  {showPw ? <FiEyeOff size={15} /> : <FiEye size={15} />}
                </button>
              }
            />

            <AnimatePresence>
              {error && (
                <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                  className="flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-body"
                  style={{ background: 'rgba(229,9,20,0.12)', border: '1px solid rgba(229,9,20,0.3)', color: '#ff6b6b' }}>
                  <FiAlertCircle size={15} /> {error}
                </motion.div>
              )}
            </AnimatePresence>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              disabled={busy}
              className="py-3.5 rounded-xl font-body font-semibold text-sm mt-1 transition-opacity"
              style={{
                background: busy ? 'rgba(229,9,20,0.5)' : 'var(--red)',
                color: '#fff',
                boxShadow: '0 0 30px rgba(229,9,20,0.3)',
              }}
            >
              {busy ? 'Please wait…' : mode === 'login' ? 'Sign In' : 'Create Account'}
            </motion.button>
          </form>

          {/* Divider */}
          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.07)' }} />
            <span className="text-xs font-body" style={{ color: 'var(--muted)' }}>or continue with</span>
            <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.07)' }} />
          </div>

          {/* Google */}
          <motion.button
            whileHover={{ scale: 1.02, borderColor: 'rgba(255,255,255,0.2)' }}
            whileTap={{ scale: 0.98 }}
            onClick={() => handle(signInGoogle)}
            disabled={busy}
            className="w-full py-3.5 rounded-xl flex items-center justify-center gap-3 font-body font-medium text-sm transition-all"
            style={{
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.09)',
              color: 'var(--text)',
            }}
          >
            <FcGoogle size={20} /> Continue with Google
          </motion.button>
        </div>
      </motion.div>
    </div>
  );
};

export default AuthPage;
